import { PieChart, Pie, Cell, ResponsiveContainer, Tooltip } from "recharts";
import { Users } from "lucide-react";
import { AddressChip, Panel } from "../trace/Primitives";
import { fmtPct, shorten } from "../../lib/trace";

const GREENS = ["#00FF66", "#00D957", "#00B348", "#008F3A", "#0A7A36", "#126B33", "#185C30", "#1B4D2B", "#1C3F26", "#1C3322"];

const colorFor = (h, i) => (h.insider ? "#F59E0B" : h.label_type ? "#475569" : GREENS[i]);

const HolderRow = ({ h, i, link }) => (
  <tr className="border-t border-trace-line/60" data-testid={`holder-row-${h.rank}`}>
    <td className="py-2.5 pr-3 font-mono text-xs text-trace-dim">{h.rank}</td>
    <td className="py-2.5 pr-3">
      <div className="flex flex-wrap items-center gap-2">
        <span className="h-2 w-2 shrink-0 rounded-sm" style={{ background: colorFor(h, i) }} />
        <AddressChip address={h.owner || h.token_account} link={link} />
        {h.label && <span className="rounded border border-trace-line px-1.5 py-0.5 text-[10px] text-trace-muted">{h.label}</span>}
        {h.insider && <span className="rounded border border-amber-500/40 px-1.5 py-0.5 text-[10px] text-amber-300">Insider (inferred)</span>}
      </div>
    </td>
    <td className="py-2.5 text-right font-mono text-xs text-white">{fmtPct(h.pct)}</td>
  </tr>
);

export const HolderDistribution = ({ data }) => {
  const h = data.holders;
  if (!h.available) {
    return (
      <Panel eyebrow="Supply distribution" title="Top holders" icon={Users} testId="holder-distribution-chart" className="lg:col-span-2">
        <p className="text-sm text-trace-muted" data-testid="holders-unavailable">Holder data is temporarily unavailable from all sources. Try refreshing in a minute.</p>
      </Panel>
    );
  }
  const chart = [...h.top.map((x, i) => ({ name: x.label || shorten(x.owner || x.token_account), value: x.pct, fill: colorFor(x, i) })),
    { name: "All other holders", value: Math.max(0, 100 - h.top10_pct), fill: "#1E2433" }];
  return (
    <Panel eyebrow={`Supply distribution · ${h.source}`} title="Top holders" icon={Users} testId="holder-distribution-chart" className="lg:col-span-2">
      <div className="grid grid-cols-1 gap-6 md:grid-cols-[220px_minmax(0,1fr)]">
        <div className="relative mx-auto h-[220px] w-[220px]">
          <ResponsiveContainer width="100%" height="100%">
            <PieChart>
              <Pie data={chart} dataKey="value" innerRadius={70} outerRadius={100} paddingAngle={1} stroke="#07080B" isAnimationActive>
                {chart.map((c) => <Cell key={c.name} fill={c.fill} />)}
              </Pie>
              <Tooltip formatter={(v) => fmtPct(v)} contentStyle={{ background: "#0D0F14", border: "1px solid #1E2433", borderRadius: 8, fontSize: 12 }} itemStyle={{ color: "#F8FAFC" }} />
            </PieChart>
          </ResponsiveContainer>
          <div className="pointer-events-none absolute inset-0 flex flex-col items-center justify-center">
            <p className="font-heading text-2xl font-semibold text-white" data-testid="top10-concentration">{fmtPct(h.top10_pct_excl_known, 1)}</p>
            <p className="max-w-[110px] text-center text-[10px] leading-tight text-trace-dim">top 10, excl. labelled pools</p>
          </div>
        </div>
        <div className="overflow-x-auto">
          <table className="w-full text-left text-sm">
            <thead><tr className="eyebrow"><th className="pb-2 font-normal">#</th><th className="pb-2 font-normal">Owner wallet</th><th className="pb-2 text-right font-normal">Supply</th></tr></thead>
            <tbody>{h.top.map((x, i) => <HolderRow key={x.rank} h={x} i={i} link={!data.is_demo} />)}</tbody>
          </table>
        </div>
      </div>
      <p className="mt-4 text-xs text-trace-dim">Grey = labelled pool/exchange account. Amber = wallet flagged as insider by heuristic (inferred).</p>
    </Panel>
  );
};
